/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LlmProvider, LlmProviderType } from '../../types';
import { llmProviderApi } from '../../services/api';
import { PageHeader, LoadingSpinner, ErrorAlert, ConfirmDialog } from '../common';
import { toast } from 'react-toastify';
import LlmProviderForm from './LlmProviderForm';

const LlmProviderList: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedProvider, setSelectedProvider] = useState<LlmProvider | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<LlmProvider | null>(null);
  const [typeFilter, setTypeFilter] = useState<LlmProviderType | ''>('');

  // Fetch providers
  const { data, isLoading, error } = useQuery({
    queryKey: ['llm-providers', typeFilter],
    queryFn: () => llmProviderApi.list({ provider_type: typeFilter || undefined }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => llmProviderApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
      toast.success('Provider deleted successfully');
      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete provider');
    },
  });

  const handleAdd = () => {
    setSelectedProvider(null);
    setFormOpen(true);
  };

  const handleEdit = (provider: LlmProvider) => {
    setSelectedProvider(provider);
    setFormOpen(true);
  };

  const handleDelete = (provider: LlmProvider) => {
    setProviderToDelete(provider);
    setDeleteConfirmOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedProvider(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
    toast.success(selectedProvider ? 'Provider updated successfully' : 'Provider created successfully');
  };

  const confirmDelete = () => {
    if (providerToDelete) {
      deleteMutation.mutate(providerToDelete.id);
    }
  };

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  return (
    <Box>
      <PageHeader
        title="LLM Providers"
        actions={
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              size="small"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as LlmProviderType | '')}
              sx={{ minWidth: { xs: 100, sm: 150 } }}
              label="Filter"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="anthropic">Anthropic</MenuItem>
              <MenuItem value="google">Google</MenuItem>
              <MenuItem value="azure">Azure</MenuItem>
              <MenuItem value="cohere">Cohere</MenuItem>
              <MenuItem value="huggingface">Hugging Face</MenuItem>
              <MenuItem value="xai">xAI (Grok)</MenuItem>
              <MenuItem value="deepseek">DeepSeek</MenuItem>
              <MenuItem value="qwen">Qwen / Alibaba Cloud</MenuItem>
              <MenuItem value="zai">Z.AI (GLM)</MenuItem>
              <MenuItem value="ollama">Ollama</MenuItem>
              <MenuItem value="vllm">vLLM</MenuItem>
              <MenuItem value="echo">Echo (Testing)</MenuItem>
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              size="small"
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Provider</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Add</Box>
            </Button>
          </Box>
        }
      />

      {isLoading ? (
        <LoadingSpinner message="Loading providers..." />
      ) : isMobile ? (
        /* Mobile Card View */
        <Grid container spacing={1.5}>
          {data?.results.map((provider) => (
            <Grid size={{ xs: 12 }} key={provider.id}>
              <Card>
                <CardContent sx={{ p: 1.5, pb: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {provider.name}
                    </Typography>
                    <Chip
                      label={provider.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={provider.is_active ? 'success' : 'default'}
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip
                      label={provider.type_display}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                    <Chip
                      label={`${provider.models_count} models`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                  <IconButton size="small" onClick={() => handleEdit(provider)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(provider)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {data?.results.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    No providers found. Click "Add" to create one.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Models</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results.map((provider) => (
                <TableRow key={provider.id} hover>
                  <TableCell>{provider.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={provider.type_display}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={provider.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={provider.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{provider.models_count} models</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(provider)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(provider)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {data?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No providers found. Click "Add Provider" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {formOpen && (
        <LlmProviderForm
          open={formOpen}
          provider={selectedProvider}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Provider"
        message={`Are you sure you want to delete "${providerToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
        confirmColor="error"
        confirmText="Delete"
      />
    </Box>
  );
};

export default LlmProviderList;
